/**
 * Pure transition query functions extracted from projectStore.
 * No store imports — these are independently testable.
 */
import type { TimelineProject, TimelineTransition, TimelineShot } from '@/types/timeline.ts';

// ── Types ──────────────────────────────────────────────────────────────

export interface TransitionAtTime {
  transition: TimelineTransition;
  fromShot: TimelineShot;
  toShot: TimelineShot;
  progress: number;
}

/** @deprecated Continuous transitions are no longer selectable in the UI. Kept for legacy project file compatibility. */
export interface ContinuousInfo {
  transition: TimelineTransition;
  fromShot: TimelineShot;
  toShot: TimelineShot;
  role: 'from' | 'to';
}

/** @deprecated Continuous transitions are no longer selectable in the UI. Kept for legacy project file compatibility. */
export interface ContinuousChainPair {
  transition: TimelineTransition;
  fromShot: TimelineShot;
  toShot: TimelineShot;
}

/** @deprecated Continuous transitions are no longer selectable in the UI. Kept for legacy project file compatibility. */
export interface ContinuousChain {
  shots: TimelineShot[];
  pairs: ContinuousChainPair[];
  shotIndex: number;
}

// ── Constants ──────────────────────────────────────────────────────────

export const TRANSITION_DURATIONS: Record<string, number> = {
  dissolve: 1.0,
  fade_to_black: 0.5,
};

// ── Pure functions ─────────────────────────────────────────────────────

export function getTransition(
  project: TimelineProject,
  fromShotId: number,
  toShotId: number,
): TimelineTransition | null {
  return project.transitions.find(
    t => t.fromShotId === fromShotId && t.toShotId === toShotId
  ) ?? null;
}

export function getTransitionAtTime(
  project: TimelineProject,
  shots: TimelineShot[],
  t: number,
): TransitionAtTime | null {
  let cumulative = 0;

  for (let i = 0; i < shots.length - 1; i++) {
    cumulative += shots[i].duration;
    const boundary = cumulative;

    const trans = project.transitions.find(
      tr => tr.fromShotId === shots[i].id && tr.toShotId === shots[i + 1].id
    );
    if (!trans || trans.type === 'cut') continue;

    const halfDur = trans.duration / 2;
    const windowStart = boundary - halfDur;
    const windowEnd = boundary + halfDur;

    if (t >= windowStart && t < windowEnd) {
      const progress = (t - windowStart) / trans.duration;
      return {
        transition: trans,
        fromShot: shots[i],
        toShot: shots[i + 1],
        progress: Math.max(0, Math.min(1, progress)),
      };
    }
  }

  return null;
}

export function getContinuousChain(
  project: TimelineProject,
  shots: TimelineShot[],
  shotId: number,
): ContinuousChain | null {
  const startIdx = shots.findIndex(s => s.id === shotId);
  if (startIdx < 0) return null;

  let left = startIdx;
  while (left > 0) {
    const trans = project.transitions.find(
      tr => tr.fromShotId === shots[left - 1].id && tr.toShotId === shots[left].id
    );
    if (!trans || trans.type !== 'continuous') break;
    left--;
  }

  let right = startIdx;
  while (right < shots.length - 1) {
    const trans = project.transitions.find(
      tr => tr.fromShotId === shots[right].id && tr.toShotId === shots[right + 1].id
    );
    if (!trans || trans.type !== 'continuous') break;
    right++;
  }

  if (left === right) return null;

  const chainShots = shots.slice(left, right + 1);
  const pairs: ContinuousChainPair[] = [];
  for (let i = left; i < right; i++) {
    const trans = project.transitions.find(
      tr => tr.fromShotId === shots[i].id && tr.toShotId === shots[i + 1].id
    )!;
    pairs.push({ transition: trans, fromShot: shots[i], toShot: shots[i + 1] });
  }

  return { shots: chainShots, pairs, shotIndex: startIdx - left };
}

export function getActiveContinuousPair(
  project: TimelineProject,
  shots: TimelineShot[],
  shotId: number,
): ContinuousChainPair | null {
  const chain = getContinuousChain(project, shots, shotId);
  if (!chain) return null;
  const pairIdx = chain.shotIndex === 0 ? 0 : chain.shotIndex - 1;
  return chain.pairs[pairIdx] ?? null;
}

export function getContinuousInfo(
  project: TimelineProject,
  shots: TimelineShot[],
  shotId: number,
): ContinuousInfo | null {
  const chain = getContinuousChain(project, shots, shotId);
  if (!chain) return null;

  if (chain.shotIndex === 0) {
    const pair = chain.pairs[0];
    return { transition: pair.transition, fromShot: pair.fromShot, toShot: pair.toShot, role: 'from' as const };
  }
  if (chain.shotIndex === chain.shots.length - 1) {
    const pair = chain.pairs[chain.pairs.length - 1];
    return { transition: pair.transition, fromShot: pair.fromShot, toShot: pair.toShot, role: 'to' as const };
  }
  const pair = chain.pairs[chain.shotIndex];
  return { transition: pair.transition, fromShot: pair.fromShot, toShot: pair.toShot, role: 'from' as const };
}
